import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { couponApi, showSuccess, showError } from "../lib";
import { X, Plus, Trash2, Copy, Check, ChevronLeft, ChevronRight } from "lucide-react";

export default function AdminCouponsPage() {
  const [coupons, setCoupons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: 10,
    maxUses: "",
    validUntil: "",
    type: "general",
    allowedEmails: [] as string[],
    courseId: "",
  });
  const [emailInput, setEmailInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchCoupons();
    fetchCourses();
  }, []);

  const fetchCoupons = async () => {
    try {
      const res = await couponApi.getAllCoupons();
      if (res.success) {
        setCoupons(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const { coursesApi } = await import("../lib");
      const res = await coursesApi.getAllCourses({ limit: 100 });
      if (res.success) {
        setCourses(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        code: formData.code,
        description: formData.description,
        discountType: formData.discountType,
        discountValue: formData.discountValue,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
        validUntil: new Date(formData.validUntil),
        type: formData.type,
        allowedEmails: formData.allowedEmails,
        courseId: formData.courseId || null,
      };
      const res = await couponApi.createCoupon(data);
      if (res.success) {
        showSuccess("Coupon created!");
        setShowForm(false);
        setFormData({
          code: "",
          description: "",
          discountType: "percentage",
          discountValue: 10,
          maxUses: "",
          validUntil: "",
          type: "general",
          allowedEmails: [],
          courseId: "",
        });
        fetchCoupons();
      }
    } catch (err: any) {
      showError(err.message || "Failed to create coupon");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this coupon?")) return;
    try {
      const res = await couponApi.deleteCoupon(id);
      if (res.success) {
        showSuccess("Coupon deleted");
        fetchCoupons();
      }
    } catch (err: any) {
      showError(err.message);
    }
  };

  const addEmail = () => {
    if (!emailInput.trim()) return;
    if (formData.allowedEmails.includes(emailInput.trim().toLowerCase())) {
      showError("Email already added");
      return;
    }
    setFormData({
      ...formData,
      allowedEmails: [...formData.allowedEmails, emailInput.trim().toLowerCase()],
    });
    setEmailInput("");
  };

  const removeEmail = (email: string) => {
    setFormData({
      ...formData,
      allowedEmails: formData.allowedEmails.filter((e) => e !== email),
    });
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    showSuccess("Copied!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-900 sm:text-xl md:text-2xl">Coupons & Promo Codes</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-0.5">Manage discount coupons and whitelist codes</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          Create Coupon
        </button>
      </div>

      {/* Coupon Form Modal - Responsive */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 sm:px-6 sm:py-4">
              <h2 className="text-base font-bold text-gray-900 sm:text-lg">Create Coupon</h2>
              <button onClick={() => setShowForm(false)} className="p-1 text-gray-400 rounded-lg hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4 sm:p-6">
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Coupon Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  placeholder="e.g., SAVE20"
                  required
                />
              </div>

              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  placeholder="Optional description"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Discount Type</label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Discount Value *</label>
                  <input
                    type="number"
                    value={formData.discountValue}
                    onChange={(e) => setFormData({ ...formData, discountValue: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                    min={1}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Max Uses</label>
                  <input
                    type="number"
                    value={formData.maxUses}
                    onChange={(e) => setFormData({ ...formData, maxUses: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                    placeholder="Empty = unlimited"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Valid Until *</label>
                  <input
                    type="date"
                    value={formData.validUntil}
                    onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">Coupon Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                >
                  <option value="general">General (anyone can use)</option>
                  <option value="whitelist">Whitelist (specific users only)</option>
                </select>
              </div>

              {formData.type === "whitelist" && (
                <div>
                  <label className="block mb-2 text-xs font-medium text-gray-700 sm:text-sm">Allowed Emails</label>
                  <div className="flex flex-col gap-2 mb-2 sm:flex-row">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                      placeholder="Enter email address"
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
                    />
                    <button
                      type="button"
                      onClick={addEmail}
                      className="px-3 py-2 text-xs text-gray-700 transition-colors bg-gray-100 rounded-lg sm:text-sm hover:bg-gray-200"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.allowedEmails.map((email) => (
                      <span
                        key={email}
                        className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700"
                      >
                        {email}
                        <button type="button" onClick={() => removeEmail(email)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {courses && courses.length > 0 && (
                <div>
                  <label className="block mb-1 text-xs font-medium text-gray-700 sm:text-sm">
                    Apply to Specific Course (optional)
                  </label>
                  <select
                    value={formData.courseId}
                    onChange={(e) => setFormData({ ...formData, courseId: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
                  >
                    <option value="">Any course</option>
                    {courses.map((course) => (
                      <option key={course._id} value={course._id}>
                        {course.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                className="w-full py-2.5 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {saving ? "Creating..." : "Create Coupon"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Coupons List */}
      {loading ? (
        <div className="py-12 text-center text-gray-500">
          <div className="inline-block w-6 h-6 border-2 rounded-full border-primary border-t-transparent animate-spin" />
          <p className="mt-2 text-sm">Loading coupons...</p>
        </div>
      ) : coupons.length === 0 ? (
        <div className="py-12 text-center bg-white border border-gray-200 rounded-xl">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full">
            <Plus className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">No coupons yet. Create one to get started!</p>
        </div>
      ) : (
        <div className="overflow-hidden bg-white border border-gray-200 rounded-xl">
          {/* Mobile Card View */}
          <div className="block divide-y divide-gray-100 lg:hidden">
            {coupons.map((coupon) => (
              <div key={coupon._id} className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{coupon.code}</span>
                    {coupon.type === "whitelist" && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Whitelist</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => copyCode(coupon.code)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
                      title="Copy code"
                    >
                      {copiedCode === coupon.code ? (
                        <Check className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(coupon._id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                
                {coupon.description && (
                  <p className="text-xs text-gray-500">{coupon.description}</p>
                )}
                
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-500">Discount:</span>
                    <span className="ml-1 font-medium">
                      {coupon.discountType === "percentage"
                        ? `${coupon.discountValue}%`
                        : `${coupon.discountValue} XAF`}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Type:</span>
                    <span className="ml-1">{coupon.courseId ? "Specific course" : "All courses"}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Uses:</span>
                    <span className="ml-1">{coupon.maxUses ? `${coupon.usedCount}/${coupon.maxUses}` : `${coupon.usedCount}/∞`}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Valid:</span>
                    <span className="ml-1">{new Date(coupon.validUntil).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <div>
                  <span
                    className={`inline-block text-[10px] px-2 py-0.5 rounded ${
                      coupon.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}
                  >
                    {coupon.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-600">Code</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-600">Discount</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-600">Type</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-600">Uses</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-600">Valid Until</th>
                  <th className="px-4 py-3 text-xs font-medium text-left text-gray-600">Status</th>
                  <th className="px-4 py-3 text-xs font-medium text-right text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {coupons.map((coupon) => (
                  <tr key={coupon._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-semibold">{coupon.code}</span>
                        {coupon.type === "whitelist" && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Whitelist</span>
                        )}
                      </div>
                      {coupon.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{coupon.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {coupon.discountType === "percentage"
                        ? `${coupon.discountValue}%`
                        : `${coupon.discountValue} XAF`}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {coupon.courseId ? "Specific course" : "All courses"}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {coupon.maxUses ? `${coupon.usedCount}/${coupon.maxUses}` : `${coupon.usedCount}/∞`}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(coupon.validUntil).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded ${
                          coupon.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}
                      >
                        {coupon.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => copyCode(coupon.code)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                          title="Copy code"
                        >
                          {copiedCode === coupon.code ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(coupon._id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}